import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { post, patch } from '../core/http';
import { API } from '../core/constants';
import { docId } from '../core/Firebase';
import CoverImage from './CoverImage';
import { ENTITY_TYPES, ENTITY_TYPE_FIELDS } from '../core/entity-types';
import Modal from './ui/Modal';

const EntityForm = ({ entity, onFormSubmit, onCancel }) => {
    const [id, setId] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [type, setType] = useState(entity?.type || ENTITY_TYPES[0].value);
    const [metadata, setMetadata] = useState(entity?.metadata || {});

    useEffect(() => {
        setId(entity?.id || docId());
        setImageUrl(entity?.thumbnail || '');
        setType(entity?.type || ENTITY_TYPES[0].value);
        setMetadata(entity?.metadata || {});
    }, [entity]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: entity,
    });

    const handleMetadataChange = (key, value) => {
        setMetadata((curr) => ({ ...curr, [key]: value }));
    };

    const onSubmit = async (data) => {
        const payload = {
            ...data,
            id,
            type,
            thumbnail: imageUrl,
            metadata,
        };

        try {
            const result = entity?.id
                ? await patch(`${API}/entities/${id}`).send(payload)
                : await post(`${API}/entities`).send(payload);

            onFormSubmit(result.body);
        } catch (error) {
            console.log(error);
        }
    };

    const fields = ENTITY_TYPE_FIELDS[type] || [];

    return (
        <Modal title={entity ? 'Editing entity' : 'Add new entity'} onClose={onCancel} wide>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                {imageUrl ? (
                    <div>
                        <img className="rounded-md mb-1.5 max-h-40 object-cover" src={imageUrl} alt="" />
                        <button type="button" className="btn btn-ghost btn-xs" onClick={() => setImageUrl(null)}>
                            Change image
                        </button>
                    </div>
                ) : (
                    <CoverImage type="entity" id={id} onUploadComplete={setImageUrl} />
                )}

                <label className="block">
                    <span className="label-text text-xs font-semibold">Name</span>
                    <input
                        className="input input-bordered input-sm w-full mt-1"
                        {...register('name', { required: 'Name is required' })}
                        placeholder="Name"
                    />
                    {errors.name && <p className="text-error text-xs mt-1">{errors.name.message}</p>}
                </label>

                <label className="block">
                    <span className="label-text text-xs font-semibold">Type</span>
                    <select
                        className="select select-bordered select-sm w-full mt-1"
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                    >
                        {ENTITY_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="block">
                    <span className="label-text text-xs font-semibold">Summary</span>
                    <textarea
                        className="textarea textarea-bordered w-full mt-1"
                        {...register('summary', { required: 'Summary is required' })}
                        placeholder="Short bio / description"
                        rows={4}
                    />
                    {errors.summary && <p className="text-error text-xs mt-1">{errors.summary.message}</p>}
                </label>

                {fields.length > 0 && (
                    <div className="border border-base-300 rounded-md p-2.5">
                        <span className="label-text text-xs font-semibold">Details ({ENTITY_TYPES.find((t) => t.value === type)?.label})</span>
                        <div className="space-y-2 mt-1.5">
                            {fields.map((field) => (
                                <label key={field.key} className="block">
                                    <span className="text-xs text-base-content/60">{field.label}</span>
                                    <input
                                        className="input input-bordered input-sm w-full mt-0.5"
                                        value={metadata[field.key] || ''}
                                        onChange={(e) => handleMetadataChange(field.key, e.target.value)}
                                        placeholder={field.label}
                                    />
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                <label className="block">
                    <span className="label-text text-xs font-semibold">Status</span>
                    <select className="select select-bordered select-sm w-full mt-1" {...register('status')}>
                        <option value="draft">Draft</option>
                        <option value="publish">Publish</option>
                    </select>
                </label>

                <div className="flex gap-2 pt-2">
                    <button type="button" className="btn btn-ghost btn-sm flex-1" onClick={() => onCancel()}>
                        Cancel
                    </button>
                    <input className="btn btn-primary btn-sm flex-1" type="submit" value={entity ? 'Save' : 'Create'} />
                </div>
            </form>
        </Modal>
    );
};

export default EntityForm;
